import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/utils/cn'
import { MeetingDatetimeTimeGrid } from './MeetingDatetimeTimeGrid'
import {
  PANEL_ESTIMATED_HEIGHT,
  STACK_TIME_BELOW_WIDTH,
  computePickerPosition,
  samePickerPosition,
  type PickerPosition,
} from './meetingDatetimePickerLayout'
import {
  clampDraftToMin,
  formatDatetimeLocal,
  isHourOptionDisabled,
  isMinuteOptionDisabled,
  minAllowedDate,
  PAST_MEETING_DATETIME_MESSAGE,
  parseDatetimeLocal,
  roundToNextMinutes,
  snapMinutesToStep,
} from './meetingDatetimeValue'

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5)

type MeetingDatetimePickerProps = {
  anchorRef: RefObject<HTMLElement | null>
  value: string
  min?: string
  dateOnly?: boolean
  onConfirm: (value: string) => void
  onClose: () => void
}

export function MeetingDatetimePicker({
  anchorRef,
  value,
  min,
  dateOnly = false,
  onConfirm,
  onClose,
}: MeetingDatetimePickerProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<PickerPosition>({
    top: 0,
    left: 0,
    width: 500,
    maxHeight: 520,
  })
  const minDate = minAllowedDate(min)

  const initial = useMemo(() => {
    const isoDay = dateOnly && /^\d{4}-\d{2}-\d{2}$/.test(value)
    const parsed = isoDay
      ? new Date(
          Number(value.slice(0, 4)),
          Number(value.slice(5, 7)) - 1,
          Number(value.slice(8, 10)),
          12,
          0,
          0,
          0,
        )
      : parseDatetimeLocal(value)
    const base = parsed
      ? snapMinutesToStep(parsed)
      : snapMinutesToStep(roundToNextMinutes(new Date(), 5))
    return clampDraftToMin(base, minDate)
  }, [value, minDate, dateOnly])

  const [draft, setDraft] = useState(initial)
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(initial))

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current
    const panel = panelRef.current
    if (!anchor) return
    const next = computePickerPosition(
      anchor.getBoundingClientRect(),
      panel?.scrollHeight ?? PANEL_ESTIMATED_HEIGHT,
      window.innerWidth,
      window.innerHeight,
    )
    if (dateOnly) next.width = Math.min(next.width, 320)
    setPosition((prev) => (samePickerPosition(prev, next) ? prev : next))
  }, [anchorRef, dateOnly])

  useLayoutEffect(() => {
    updatePosition()
  }, [updatePosition])

  useEffect(() => {
    const panel = panelRef.current
    const onViewportChange = () => updatePosition()
    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)
    const observer =
      panel && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(onViewportChange)
        : null
    if (panel && observer) observer.observe(panel)
    return () => {
      window.removeEventListener('resize', onViewportChange)
      window.removeEventListener('scroll', onViewportChange, true)
      observer?.disconnect()
    }
  }, [updatePosition])

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (panelRef.current?.contains(target)) return
      if (anchorRef.current?.contains(target)) return
      onClose()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [anchorRef, onClose])

  const monthStart = startOfMonth(viewMonth)
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 })
  const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 0 })
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd })
  const stackTimeBelow = position.width < STACK_TIME_BELOW_WIDTH

  const isDayDisabled = (day: Date) => {
    if (!minDate) return false
    return isBefore(startOfDay(day), startOfDay(minDate))
  }

  const patchDraft = (patch: Partial<{ y: number; m: number; d: number; h: number; min: number }>) => {
    setDraft((prev) => {
      const next = new Date(prev)
      if (patch.y !== undefined) next.setFullYear(patch.y)
      if (patch.m !== undefined) next.setMonth(patch.m)
      if (patch.d !== undefined) next.setDate(patch.d)
      if (patch.h !== undefined) next.setHours(patch.h)
      if (patch.min !== undefined) next.setMinutes(patch.min)
      return clampDraftToMin(next, minDate)
    })
  }

  const notifyPastDatetime = useCallback(() => {
    toast.error(PAST_MEETING_DATETIME_MESSAGE)
  }, [])

  const content = (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={dateOnly ? 'Selecionar data' : 'Selecionar data e hora'}
      className="fixed z-[200] flex flex-col overflow-hidden rounded-xl border border-white/[0.08] bg-bg-main shadow-2xl"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: position.maxHeight,
      }}
    >
      <div className={cn('flex min-h-0 flex-1', stackTimeBelow ? 'flex-col' : 'flex-row')}>
        <div
          className={cn(
            'min-h-0 p-3',
            stackTimeBelow
              ? 'flex-1 overflow-y-auto overscroll-contain border-b border-white/[0.06]'
              : 'flex-1 border-r border-white/[0.06]',
          )}
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewMonth((m) => subMonths(m, 1))}
              className="rounded-lg p-1.5 text-text-muted hover:bg-white/[0.06] hover:text-text-primary"
              aria-label="Mês anterior"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm font-semibold capitalize text-text-primary">
              {format(viewMonth, 'MMMM yyyy', { locale: ptBR })}
            </span>
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="rounded-lg p-1.5 text-text-muted hover:bg-white/[0.06] hover:text-text-primary"
              aria-label="Próximo mês"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="mb-0.5 grid grid-cols-7 gap-0.5">
            {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((w, i) => (
              <span
                key={`${w}-${i}`}
                className="py-0.5 text-center text-[9px] font-bold uppercase text-text-muted"
              >
                {w}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {days.map((day) => {
              const disabled = isDayDisabled(day)
              const selected = isSameDay(day, draft)
              const outside = !isSameMonth(day, viewMonth)
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    patchDraft({
                      y: day.getFullYear(),
                      m: day.getMonth(),
                      d: day.getDate(),
                    })
                  }}
                  className={cn(
                    'flex h-7 items-center justify-center rounded-md text-xs font-medium transition-colors',
                    disabled && 'cursor-not-allowed opacity-30',
                    outside && 'text-text-muted/50',
                    !disabled && !selected && 'text-text-secondary hover:bg-white/[0.06]',
                    selected &&
                      'border-2 border-warning bg-accent text-white hover:bg-accent',
                  )}
                >
                  {format(day, 'd')}
                </button>
              )
            })}
          </div>

          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => onConfirm('')}
              className="text-xs font-medium text-text-muted hover:text-text-primary"
            >
              Limpar
            </button>
            <button
              type="button"
              onClick={() => {
                const now = snapMinutesToStep(roundToNextMinutes(new Date(), 5))
                const next = clampDraftToMin(now, minDate)
                setDraft(next)
                setViewMonth(startOfMonth(next))
              }}
              className="text-xs font-medium text-accent hover:underline"
            >
              Hoje
            </button>
          </div>
        </div>

        {!dateOnly && (
        <div
          className={cn(
            'flex shrink-0 p-3',
            stackTimeBelow ? 'w-full border-b border-white/[0.06]' : 'w-[252px] border-l border-white/[0.06]',
          )}
        >
          <div className="grid w-full grid-cols-2 gap-2">
            <MeetingDatetimeTimeGrid
              label="Hora"
              options={HOURS}
              selected={draft.getHours()}
              formatOption={(h) => String(h).padStart(2, '0')}
              disabledOption={(h) => isHourOptionDisabled(draft, h, minDate)}
              onBlocked={notifyPastDatetime}
              onSelect={(h) => patchDraft({ h })}
            />
            <MeetingDatetimeTimeGrid
              label="Min"
              options={MINUTES}
              selected={draft.getMinutes()}
              formatOption={(m) => String(m).padStart(2, '0')}
              disabledOption={(m) => isMinuteOptionDisabled(draft, m, minDate)}
              onBlocked={notifyPastDatetime}
              onSelect={(m) => patchDraft({ min: m })}
            />
          </div>
        </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-white/[0.06] bg-bg-main px-3 py-2.5">
        <p className="truncate text-xs text-text-muted">
          {format(draft, dateOnly ? 'EEE, dd MMM yyyy' : "EEE, dd MMM yyyy · HH:mm", { locale: ptBR })}
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-white/[0.08] px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-white/[0.04]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(dateOnly ? format(draft, 'yyyy-MM-dd') : formatDatetimeLocal(draft))}
            className="rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
